import React, { useState, useEffect, useRef, useContext } from 'react';
import { InvestmentStrategyType } from '../../../model/CalculatorInputs';
import { pipe } from 'fp-ts/lib/pipeable';
import { fold, Option } from 'fp-ts/lib/Option';
import { CalculatorStepsContext } from '../../Store/CalculatorStepsContext';

interface Props {
    index: number;
    validationIssue: string;

    availableStrategyTypes: Array<InvestmentStrategyType>;
    strategyType: Option<InvestmentStrategyType>;
    selectStrategyType: (strategyTypeCode: string) => void;
    removeStrategy: () => void;
    invalid: boolean;
    isDuplicateStrategy: boolean;
    validationMessageId: string;
}


export const SelectStrategyType = (props: Props) => {

    const initialCode = pipe(props.strategyType,
        fold(() => 'none',
            (st) => st.code));
    const [strategyCode, setStrategyCode] = useState<string>(initialCode);
    const availableCodes = props.availableStrategyTypes.map(st => st.code);
    const selectRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
        // this is required for re-rendering when a strategy is removed
        setStrategyCode(initialCode);
        if (props.index !== 0) {
            selectRef.current?.focus();
        }

    }, [initialCode]);

    useEffect(() => {
        if (strategyCode !== 'none' && !availableCodes.includes(strategyCode)) {
            props.selectStrategyType('none');
        }
    }, [availableCodes])

    const { steps } = useContext(CalculatorStepsContext);
    useEffect(() => {
        const grabFocus = async () => {
            pipe(steps.firstValidationError,
                fold(() => {
                    return;
                },
                    (firstError) => {
                        if (firstError === props.validationIssue) {
                            if (selectRef.current !== null) {
                                selectRef.current.focus();
                            }
                        }
                    }));
        };
        setTimeout(grabFocus, 25);
    }, [steps.firstValidationError])

    const strategyLabel = `Strategy ${props.index + 1}`;
    const selectId = `rila-calculator-strategy-selector-${props.index + 1}`;
    const strategySelectorClasses = ["strategy-type-selector", "strategy-row", `strategy-row-${props.index + 1}`];
    const strategyLabelClasses = ['strategy-label'];


    if (props.invalid) {
        strategySelectorClasses.push('validation-error');
    }
    if (props.isDuplicateStrategy) {
        strategySelectorClasses.push('duplicate-strategy');
        strategyLabelClasses.push('duplicate-strategy');
    }

    

    function handleSelectionChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const selectedCode = event.target.value;
        setStrategyCode(selectedCode);
        props.selectStrategyType(selectedCode)
    }


    const describedBy = props.invalid ? props.validationMessageId : undefined;


    return (
        <div className={strategySelectorClasses.join(' ')}>

    <label htmlFor={selectId} className={strategyLabelClasses.join(' ')}>{strategyLabel}</label>
            <div className="choose-or-remove-strategy">
                <select id={selectId}
                    aria-label={strategyLabel}
                    aria-describedby={describedBy}
                    data-testid={selectId}
                    className="form-control"
                    value={strategyCode}
                    ref={selectRef}
                    onChange={handleSelectionChange}
                    autoComplete="off">
                    <option value="none">Select a strategy</option>
                    {props.availableStrategyTypes.map(st => <option value={st.code} key={st.code}>{st.label}</option>)}
                </select>
                {(props.index === 0) ? null : <button className="btn btn-link" onClick={() => props.removeStrategy()}>Remove</button>}
            </div>
        </div>
    )
}

